/**
 * Labels and floater text for the CTS markers: the confluence index, and the configuration badges.
 *
 * <p>Pure functions over a scan day. The detection itself is in the backend
 * ({@code GannPatterns}) — a day arrives already carrying {@code confluenceIndex} and
 * {@code patterns}, and this decides what of that fits on a triangle and what belongs in the card.
 *
 * <p>Detection is NOT duplicated here on purpose. Both the live scan and the precomputed timeline
 * reach the same Java, so the two paths cannot come to different answers about the same day; a
 * second implementation in JavaScript would be a second answer waiting to happen.
 */

// From visualUniverseAspects, NOT from gannConfluenceOverlay's identical copy: that module now
// imports this one, and pointing back at it would close a cycle. The two abbreviation tables are
// the same table, so nothing about the output changes.
import { abbreviate } from './visualUniverseAspects';

/**
 * The index written on a triangle: which non-trine aspect the trine is paired with.
 *
 * <p>Conjunction 0, square 1, opposition 2. The names are carried too, because the digit is a
 * shorthand and a reader meeting it for the first time needs the card to expand it.
 */
export const CONFLUENCE_INDEX_NAMES = {
  0: 'Conjunction + Trine',
  1: 'Square + Trine',
  2: 'Opposition + Trine',
};

/**
 * The badge letters, with the names they stand for.
 *
 * <p>The backend sends {@code name} and {@code code} on every match, so this table is not what
 * renders a found configuration — it is what lets the legend and the tests name a letter without a
 * scan, and what fills in if an older backend sends a letter and nothing else.
 */
export const PATTERN_META = {
  A: { code: 'GT', name: 'Grand Trine' },
  B: { code: 'GC', name: 'Grand Cross' },
  C: { code: 'TS', name: 'T-Square' },
  D: { code: '4S', name: 'Four-Planet Square Ring' },
  E: { code: '3C', name: 'Stellium' },
  F: { code: 'SM', name: 'Simultaneous Multi-Aspect' },
  G: { code: 'KT', name: 'Kite' },
  H: { code: 'MY', name: 'Mystic Rectangle' },
  I: { code: 'YD', name: 'Yod' },
};

/**
 * How many letters a badge may show before it counts the rest.
 *
 * <p>Four. Measured over 2000-2024, an orb-3 run marks 29% of days and 95% of those marks carry at
 * least one configuration, so a badge is the common case rather than the rare one — and a badge as
 * long as the date beside it stops being a badge. The card carries every one of them.
 */
export const MAX_BADGE_LETTERS = 4;

/** The index digit for a day, or null when the day did not carry one. */
export function indexDigit(day) {
  const i = day && day.confluenceIndex;
  return typeof i === 'number' && i >= 0 && i <= 2 ? String(i) : null;
}

/** The full name behind an index digit. */
export function indexName(day) {
  const i = day && day.confluenceIndex;
  return CONFLUENCE_INDEX_NAMES[i] || '';
}

/** The distinct letters standing on a day, in alphabetical order. */
export function patternLetters(day) {
  const list = Array.isArray(day && day.patterns) ? day.patterns : [];
  const seen = [];
  list.forEach((p) => {
    if (p && typeof p.letter === 'string' && !seen.includes(p.letter)) seen.push(p.letter);
  });
  return seen.sort();
}

/**
 * The badge drawn beside a marker, or null when the day carried no configuration.
 *
 * <p>Alphabetical rather than tightest-first, which is a deliberate choice against the ordering
 * used everywhere else here. A badge is read at a glance and compared against its neighbours, so
 * the same set of letters must always look the same; ordering by tightness would make "ACF" and
 * "CAF" the same finding on two bars and defeat the comparison the badge exists for.
 */
export function badgeText(day) {
  const letters = patternLetters(day);
  if (letters.length === 0) return null;
  if (letters.length <= MAX_BADGE_LETTERS) return letters.join('');
  const shown = letters.slice(0, MAX_BADGE_LETTERS).join('');
  return `${shown}+${letters.length - MAX_BADGE_LETTERS}`;
}

/**
 * An aspect written as one aligned line: bodies, angle, and how far off exact it is.
 *
 * <p>Two decimals, not the whole degrees the wheel's summary uses. CTS runs at an orb of 1 to 3, so
 * every deviation rounds to 0 there — the column would be a row of zeros and the "observed orb
 * deviation" the card is meant to show would be missing from it.
 */
export function legLine(leg) {
  if (!leg) return '';
  const a = abbreviate(leg.a).padEnd(2);
  const b = abbreviate(leg.b).padEnd(2);
  const angle = `${Math.round(leg.angle)}°`.padStart(4);
  const orb = typeof leg.orb === 'number' ? leg.orb.toFixed(2) : '?';
  return `${a} ${angle} ${b}  ${`±${orb}°`.padStart(7)}`;
}

/**
 * The card shown on a configuration badge: what each shape is, out of what, and how tight.
 *
 * <p>Every leg of every match, not a summary of them. The point of a badge is that a reader can
 * check it — "these four bodies, at these four angles, each this far off" is checkable against the
 * wheel, and "Grand Cross, orb 1.4" is not.
 */
export function patternTooltipLines(day) {
  const list = Array.isArray(day && day.patterns) ? day.patterns : [];
  if (list.length === 0) return [];

  const out = [];
  list.forEach((p, i) => {
    if (!p) return;
    if (i > 0) out.push('');
    const meta = PATTERN_META[p.letter] || {};
    const name = p.name || meta.name || p.letter;
    const code = p.code || meta.code || '';
    const bodies = Array.isArray(p.bodies) ? p.bodies.join(', ') : '';
    out.push(`${p.letter} · ${name}${code ? ` (${code})` : ''}`);
    if (bodies) out.push(`   ${bodies}`);
    (Array.isArray(p.legs) ? p.legs : []).forEach((leg) => out.push(`   ${legLine(leg)}`));
  });

  // What the per-letter cap hid. A list that is short because it was truncated must not read as a
  // list that is short because the sky was quiet.
  const census = (day && day.patternCensus) || {};
  const hidden = Object.keys(census)
    .map((letter) => {
      const found = census[letter] || 0;
      const shown = list.filter(p => p && p.letter === letter).length;
      return found > shown ? `${letter}×${found - shown}` : null;
    })
    .filter(Boolean);
  if (hidden.length > 0) {
    out.push('');
    out.push(`… ${hidden.join(' ')} more not listed`);
  }
  return out;
}

/**
 * The whole floater for a CTS marker: the date, the confluence it was marked for, and the shapes.
 *
 * <p>The date leads, because that is what the marker has always said and what a reader hovering a
 * snapped arrow most needs — the day the scan fired, which is not always the bar it is drawn on.
 */
export function markerTooltipLines(day, aspectLines) {
  const out = [];
  const digit = indexDigit(day);
  if (digit !== null) out.push(`${digit} · ${indexName(day)}`);

  const others = Array.isArray(day && day.confluenceIndices)
    ? day.confluenceIndices.filter(i => String(i) !== digit)
    : [];
  if (others.length > 0) {
    // A day can carry more than one shape of confluence. The triangle shows the tightest; saying so
    // is what stops the digit reading as "this day had only this".
    out.push(`    also ${others.map(i => `${i} ${CONFLUENCE_INDEX_NAMES[i] || ''}`.trim()).join(', ')}`);
  }

  const aspects = Array.isArray(aspectLines) ? aspectLines : [];
  if (aspects.length > 0) {
    if (out.length > 0) out.push('');
    out.push(...aspects);
  }

  const patterns = patternTooltipLines(day);
  if (patterns.length > 0) {
    if (out.length > 0) out.push('');
    out.push(...patterns);
  }
  return out;
}
